/* An opt-in product theater. Without this enhancement, every scene stays visible. */
(() => {
  'use strict';

  window.TrailCrashersShowcase?.destroy();
  const selector = '[data-tc-showcase]';
  const instances = new Map();
  const listeners = [];
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px) and (pointer: fine)');
  const motionProperties = ['--tc-tilt-x', '--tc-tilt-y', '--tc-drift-x', '--tc-drift-y', '--tc-stage-scroll'];
  const ownedClasses = ['tc-showcase-ready', 'is-detail', 'is-playing'];
  let observer;
  let settingsObserver;
  let started = false;
  let destroyed = false;

  const mode = () => document.body?.dataset.tcMotion || 'full';
  const canAnimate = () => !reduced.matches && mode() !== 'off' && !window.Shopify?.designMode;
  const canTilt = () => canAnimate() && mode() === 'full' && desktop.matches && !document.hidden;
  const clamp = (number, limit) => Math.max(-limit, Math.min(limit, number));
  const inViewport = (root) => {
    const box = root.getBoundingClientRect();
    return box.bottom > 0 && box.top < window.innerHeight;
  };

  function listen(target, event, callback, options, cleanup = listeners) {
    target.addEventListener(event, callback, options);
    cleanup.push(() => target.removeEventListener(event, callback, options));
  }

  function watch(query, callback) {
    if (query.addEventListener) listen(query, 'change', callback);
    else {
      query.addListener(callback);
      listeners.push(() => query.removeListener(callback));
    }
  }

  function rememberAttribute(element, name, cleanup) {
    if (!element) return;
    const value = element.getAttribute(name);
    cleanup.push(() => {
      if (value === null) element.removeAttribute(name);
      else element.setAttribute(name, value);
    });
  }

  function create(root) {
    if (instances.has(root)) return;
    const scenes = Array.from(root.querySelectorAll('[data-tc-scene]'));
    const stage = root.querySelector('[data-tc-stage]');
    const buttons = Array.from(root.querySelectorAll('[data-tc-scene-select]'));
    const playButton = root.querySelector('[data-tc-play]');
    const originalPlayTitle = playButton?.getAttribute('title');
    const playLabel = root.querySelector('[data-tc-play-label]');
    const detailButton = root.querySelector('[data-tc-detail]');
    const status = root.querySelector('[data-tc-showcase-status]');
    if (!stage || !scenes.length || !buttons.length) return;

    const cleanup = [];
    const state = { root, scenes, active: 0, visible: inViewport(root), playing: false, timer: 0, frame: 0, pointer: null };
    const originalClasses = ownedClasses.map((name) => [name, root.classList.contains(name)]);
    const originalStyles = [...motionProperties, '--tc-showcase-accent'].map((name) =>
      [name, root.style.getPropertyValue(name), root.style.getPropertyPriority(name)]
    );
    rememberAttribute(root, 'data-active', cleanup);
    scenes.forEach((scene) => {
      rememberAttribute(scene, 'aria-hidden', cleanup);
      rememberAttribute(scene, 'inert', cleanup);
      const wasActive = scene.classList.contains('is-active');
      cleanup.push(() => scene.classList.toggle('is-active', wasActive));
    });
    buttons.forEach((button) => rememberAttribute(button, 'aria-pressed', cleanup));
    rememberAttribute(playButton, 'aria-pressed', cleanup);
    rememberAttribute(playButton, 'aria-disabled', cleanup);
    rememberAttribute(playButton, 'title', cleanup);
    rememberAttribute(detailButton, 'aria-pressed', cleanup);
    [playLabel, status].forEach((element) => {
      if (!element) return;
      const originalText = element.textContent;
      cleanup.push(() => { element.textContent = originalText; });
    });

    function updatePlayControl() {
      const disabledReason = reduced.matches ? 'Automatic playback is off for your reduced motion preference.'
        : mode() === 'off' ? 'Automatic playback is turned off.'
          : window.Shopify?.designMode ? 'Automatic playback is paused in the theme editor.'
            : scenes.length < 2 ? 'Only one design is available.' : '';
      root.classList.toggle('is-playing', state.playing);
      playButton?.setAttribute('aria-pressed', String(state.playing));
      playButton?.setAttribute('aria-disabled', String(Boolean(disabledReason)));
      if (disabledReason) playButton?.setAttribute('title', disabledReason);
      else if (originalPlayTitle != null) playButton?.setAttribute('title', originalPlayTitle);
      else playButton?.removeAttribute('title');
      if (playLabel) playLabel.textContent = state.playing ? 'Pause showcase' : 'Play showcase';
    }

    function pause() {
      clearTimeout(state.timer);
      state.timer = 0;
      state.playing = false;
      updatePlayControl();
    }

    function select(index, manual = false) {
      if (!Number.isInteger(index) || index < 0 || index >= scenes.length) return;
      if (manual) pause();
      state.active = index;
      root.dataset.active = String(index);
      scenes.forEach((scene, position) => {
        const active = position === index;
        scene.classList.toggle('is-active', active);
        scene.setAttribute('aria-hidden', String(!active));
        scene.toggleAttribute('inert', !active);
      });
      buttons.forEach((button) => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.tcSceneSelect) === index));
      });
      const accent = scenes[index].dataset.accent;
      if (accent) root.style.setProperty('--tc-showcase-accent', accent);
      else root.style.removeProperty('--tc-showcase-accent');
      if (manual && status) {
        status.textContent = `${scenes[index].dataset.title || 'Design'} · ${index + 1} of ${scenes.length}`;
      }
    }

    function scheduleNext() {
      clearTimeout(state.timer);
      state.timer = window.setTimeout(() => {
        state.timer = 0;
        // Never retire a scene while somebody is using a link or form inside it.
        if (!state.playing || !canAnimate() || document.hidden || !inViewport(root) || scenes[state.active].contains(document.activeElement)) {
          pause();
          return;
        }
        select((state.active + 1) % scenes.length);
        scheduleNext();
      }, 4500);
    }

    function togglePlay() {
      if (state.playing) {
        pause();
        return;
      }
      if (!canAnimate() || document.hidden || !inViewport(root) || scenes.length < 2) return;
      root.classList.remove('is-detail');
      detailButton?.setAttribute('aria-pressed', 'false');
      state.playing = true;
      updatePlayControl();
      scheduleNext();
    }

    function resetSpatial() {
      cancelAnimationFrame(state.frame);
      state.frame = 0;
      state.pointer = null;
      motionProperties.forEach((property) => root.style.removeProperty(property));
    }

    function updateSpatial() {
      state.frame = 0;
      if (!state.visible || !canTilt()) return;
      const box = stage.getBoundingClientRect();
      const position = (window.innerHeight / 2 - (box.top + box.height / 2)) / ((window.innerHeight + box.height) / 2);
      root.style.setProperty('--tc-stage-scroll', clamp(position, 1).toFixed(3));
      if (!state.pointer || !box.width || !box.height) return;
      const x = clamp((state.pointer.x - box.left) / box.width * 2 - 1, 1);
      const y = clamp((state.pointer.y - box.top) / box.height * 2 - 1, 1);
      root.style.setProperty('--tc-tilt-x', `${(-y * 5).toFixed(2)}deg`);
      root.style.setProperty('--tc-tilt-y', `${(x * 5).toFixed(2)}deg`);
      root.style.setProperty('--tc-drift-x', `${(x * 14).toFixed(2)}px`);
      root.style.setProperty('--tc-drift-y', `${(y * 14).toFixed(2)}px`);
    }

    function scheduleSpatial() {
      if (!state.frame && state.visible && canTilt()) state.frame = requestAnimationFrame(updateSpatial);
    }

    function conditionsChanged() {
      if (!canAnimate() || document.hidden || !state.visible) pause();
      else updatePlayControl();
      if (!canTilt() || !state.visible) resetSpatial();
      else scheduleSpatial();
    }

    buttons.forEach((button) => {
      listen(button, 'click', () => select(Number(button.dataset.tcSceneSelect), true), undefined, cleanup);
    });
    if (playButton) listen(playButton, 'click', togglePlay, undefined, cleanup);
    if (detailButton) {
      listen(detailButton, 'click', () => {
        pause();
        const detail = root.classList.toggle('is-detail');
        detailButton.setAttribute('aria-pressed', String(detail));
        if (status) status.textContent = detail ? 'Artwork detail view' : 'Full garment view';
      }, undefined, cleanup);
    }
    listen(root, 'focusin', (event) => {
      if (state.playing && scenes.some((scene) => scene.contains(event.target))) pause();
    }, undefined, cleanup);
    listen(stage, 'pointermove', (event) => {
      if (event.pointerType === 'touch' || !canTilt()) return;
      state.pointer = { x: event.clientX, y: event.clientY };
      scheduleSpatial();
    }, { passive: true }, cleanup);
    listen(stage, 'pointerleave', () => {
      state.pointer = null;
      motionProperties.slice(0, 4).forEach((property) => root.style.removeProperty(property));
    }, { passive: true }, cleanup);

    state.pause = pause;
    state.select = select;
    state.conditionsChanged = conditionsChanged;
    state.scheduleSpatial = scheduleSpatial;
    state.destroy = () => {
      pause();
      resetSpatial();
      observer?.unobserve(root);
      cleanup.forEach((restore) => restore());
      originalClasses.forEach(([name, present]) => root.classList.toggle(name, present));
      originalStyles.forEach(([name, value, priority]) => {
        if (value) root.style.setProperty(name, value, priority);
        else root.style.removeProperty(name);
      });
      instances.delete(root);
    };
    instances.set(root, state);
    root.classList.remove('is-detail');
    detailButton?.setAttribute('aria-pressed', 'false');
    select(0);
    updatePlayControl();
    root.classList.add('tc-showcase-ready');
    observer?.observe(root);
    conditionsChanged();
  }

  function refresh(scope = document) {
    if (destroyed || !started || !scope.querySelectorAll) return;
    const roots = Array.from(scope.querySelectorAll(selector));
    if (scope.matches?.(selector)) roots.unshift(scope);
    roots.forEach(create);
  }

  function onScroll() {
    instances.forEach((state) => {
      if (!observer) {
        const visible = inViewport(state.root);
        if (visible !== state.visible) {
          state.visible = visible;
          state.conditionsChanged();
        }
      }
      state.scheduleSpatial();
    });
  }

  function start() {
    if (started || destroyed || !document.body) return;
    started = true;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const state = instances.get(entry.target);
          if (!state) return;
          state.visible = entry.isIntersecting;
          state.conditionsChanged();
        });
      }, { threshold: 0 });
    }
    const updateConditions = () => instances.forEach((state) => state.conditionsChanged());
    listen(window, 'scroll', onScroll, { passive: true });
    listen(window, 'resize', onScroll, { passive: true });
    listen(document, 'visibilitychange', updateConditions);
    listen(window, 'pagehide', () => instances.forEach((state) => state.pause()));
    listen(window, 'pageshow', () => { refresh(); updateConditions(); });
    watch(reduced, updateConditions);
    watch(desktop, updateConditions);
    listen(document, 'shopify:section:load', (event) => refresh(event.target));
    listen(document, 'shopify:section:unload', (event) => {
      instances.forEach((state) => { if (event.target.contains(state.root)) state.destroy(); });
    });
    listen(document, 'shopify:section:select', (event) => {
      instances.forEach((state) => { if (event.target.contains(state.root)) state.pause(); });
    });
    listen(document, 'shopify:block:select', (event) => {
      const targetScene = event.target.closest?.('[data-tc-scene]');
      const blockId = event.detail?.blockId;
      instances.forEach((state) => {
        const index = state.scenes.findIndex((scene) => scene === targetScene || (blockId && scene.dataset.blockId === blockId));
        if (index !== -1) state.select(index, true);
      });
    });
    if ('MutationObserver' in window) {
      settingsObserver = new MutationObserver(updateConditions);
      settingsObserver.observe(document.body, { attributes: true, attributeFilter: ['data-tc-motion'] });
    }
    refresh();
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    document.removeEventListener('DOMContentLoaded', start);
    instances.forEach((state) => state.destroy());
    listeners.forEach((remove) => remove());
    observer?.disconnect();
    settingsObserver?.disconnect();
  }

  window.TrailCrashersShowcase = { refresh, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
